import { create } from 'zustand'
import type { AiResultsResponse, FieldMetadata, FieldResponsePayload, FormValues, MappedField, PhotoItem, SectionState } from '@/types'
import type { ClaimSectionImage } from '@/types/claim'
import { SECTIONS } from '@/data/sections.data'
import { generateId } from '@/utils/formatters'
import { v4 as uuidv4 } from 'uuid'
import {
  saveDraftMetadata,
  loadDraftMetadata,
  clearAllDraftData,
  saveImageRecord,
  loadAllImageRecords,
  deleteImageRecord,
  clearAllImageRecords,
  updateImageServerName,
} from '@/services/idbService'
import {
  getClaim,
  deleteClaimPhotosBySection,
  deleteClaimPhotoByName,
  deleteAllClaimPhotos,
  getClaimImageList,
  getClaimImageFile,
} from '@/services/claimService'
import { claimResponseToFormValues } from '@/utils/claimPayloadMapper'
import { preloadRefCategories } from '@/utils/preloadRefCategories'
import { useRefStore } from '@/store/refStore'
import { useAttributeStore } from './attributeStore'
import { useUiStore } from '@/store/uiStore'
import { useAuthStore } from '@/store/authStore'
import { getMessage } from '@/utils/messages'
import { computeFileHash } from '@/utils/fileHash'
import { log } from '@/utils/logger'
import { VEHICLE_TYPE_SECTIONS } from '@/data/vehicleTypeSections'

// Build initial section state from section definitions
function buildInitialSectionStates(): Record<string, SectionState> {
  return Object.fromEntries(
    SECTIONS.map((s) => [s.id, { expanded: false, complete: false, touched: false }])
  )
}

interface ValuationState {
  // Hydration flag — true once IDB data has been loaded
  hydrated: boolean

  // True while an existing claim's data is being fetched/mapped into the form
  // (e.g. Edit-from-Dashboard via loadClaimForEditing). Lets effects that watch
  // dependent fields (like Use/Type) tell "claim data is loading in" apart from
  // a genuine user-driven change, so they don't reset sections that just got populated.
  isLoadingClaim: boolean
  setIsLoadingClaim: (v: boolean) => void

  // Draft identity
  draftId: string | null
  caseId: string | null
  status: 'draft' | 'submitted' | 'processing' | 'complete'

  // Section UI state
  sectionStates: Record<string, SectionState>

  // Form field values: { sectionId: { fieldId: value } }
  formValues: FormValues

  // Photos for the current draft
  photos: PhotoItem[]
  
  // True when form/photo changes have not yet been saved to the backend
  isDirty: boolean
  setIsDirty: (v: boolean) => void

  // True once the user has genuinely changed a field or photo within a vehicle-type
  // section (standard-equipment/additional-options/body-information/vehicle-condition)
  // since the vehicle type was last (re)selected. Drives the change-confirmation popup —
  // selecting a vehicle type or saving/reloading a draft must never set this on its own.
  vehicleSectionsDirty: boolean
  markVehicleSectionsDirty: () => void
  clearVehicleSectionsDirty: () => void

  // Actions — hydration
  hydrateFromIDB: () => Promise<void>
  /** Mark the store as already hydrated (skips IDB read). Call after loading from server on login. */
  markHydrated: () => void
  /** Cancel the pending debounced save and flush store state to IDB immediately. */
  flushSave: () => Promise<void>

  // Actions — sections
  expandSection: (id: string) => void
  collapseSection: (id: string) => void
  toggleSection: (id: string) => void
  markSectionComplete: (id: string) => void

  // Actions — form fields
  setFieldValue: (sectionId: string, fieldId: string, value: string, autoSet?: boolean) => void

  // Actions — photos (blob-first)
  addPhoto: (input: {
    sectionId: string
    blob: Blob
    type: 'capture' | 'upload'
    name?: string
    mimeType?: string
    serverName?: string
    blobSection?: string
    contentHash?: string
  }) => string
  removePhoto: (photoId: string, options?: { forceDelete?: boolean }) => Promise<void>
  /** Remove a photo from IDB and UI only — skips the blob API call (used when the
   *  backend has already cleaned up blob storage, e.g. after an AI processing failure). */
  removePhotoLocal: (photoId: string) => void
  clearAllPhotos: () => void
  clearSectionPhotos: (sectionId: string) => void
  loadPhotosFromServer: (userId: string, caseId: string, sectionId: string, blobSection?: string) => Promise<void>
  loadAllPhotosFromServer: (userId: string, caseId: string) => Promise<void>
  updatePhotoServerNames: (userId: string, caseId: string, sectionId: string, blobSection?: string) => Promise<void>
  setPhotoServerInfo: (photoId: string, info: { serverName: string; blobSection?: string }) => void

  // Actions — draft lifecycle
  setDraftId: (id: string) => void
  setCaseId: (id: string) => void
  setStatus: (status: ValuationState['status']) => void
  setClaimData: (claimId: string, populated: FormValues, baseline?: FormValues) => void
  loadClaimFromApi: (claimId: string) => Promise<void>
  resetForm: () => void
  resetSectionFields: (sectionId: string) => void
  clearLocalDraft: () => void

  // Submit validation flag
  hasTriedSubmit: boolean
  setHasTriedSubmit: (value: boolean) => void

  // Save validation flag (highlights only save-required fields)
  hasTriedSave: boolean
  setHasTriedSave: (value: boolean) => void

  // AI / OCR field metadata: { sectionId: { fieldId: FieldMetadata } }
  fieldMetadata: Record<string, Record<string, FieldMetadata>>
  applyAIFields: (fields: MappedField[], forceOverwrite?: boolean) => void
  applyFieldMetadata: (metadata: Record<string, Record<string, FieldMetadata>>) => void
  clearFieldMetadata: () => void
  clearRawAIValue: (sectionId: string, fieldId: string) => void
  setAiValue: (sectionId: string, fieldId: string, value: string) => void
  setScanResult: (sectionId: string, fieldId: string, value: string) => void
  clearScanResult: (sectionId: string, fieldId: string) => void

  
  // Last AI result per section — persisted so View AI Data survives refresh
  aiResults: Record<string, MappedField[]>
  setAiResult: (sectionId: string, fields: MappedField[]) => void
  // Raw AI payloads per photo section — used to re-map body-information fields once they load
  aiRawPayloads: Record<string, FieldResponsePayload>
  setAiRawPayload: (photoSectionId: string, payload: FieldResponsePayload) => void
  // Clears only the specified fields from formValues and fieldMetadata (used when removing a photo)
  clearSpecificFields: (fields: Array<{ sectionId: string; fieldKey: string }>) => void
  // Promotes the confidence score on existing fieldMetadata entries — used after a photo is removed
  // so the displayed confidence badge reflects the best remaining image, not the deleted one.
  promoteFieldConfidence: (updates: Array<{ sectionId: string; fieldKey: string; confidence: number }>) => void
  // Clears all AI data (metadata, results, AI-contributed values) for one section
  clearSectionAIData: (sectionId: string) => void
  // Clears only aiResults/aiRawPayloads/photoResults for a section — does NOT touch form values (used when removing a single photo, where clearSpecificFields already handled field cleanup)
  clearSectionAIMetadata: (sectionId: string) => void
  // Clears OCR data (mapped fields + metadata + ocrResults) for one section
  clearSectionOcrData: (sectionId: string) => void
  // Clears only ocrResults/photoResults for a section — does NOT touch form values (used when removing a single OCR photo)
  clearSectionOcrMetadata: (sectionId: string) => void
  // Clears all AI data across all sections (used on vehicle-type change and new inspection)
  clearAllAIData: () => void
  // Clears body-information AI data without touching other sections (used on Use/Type change)
  clearBodyInfoAIData: () => void

  // Aggregated OCR result per section (merged across all OCR images in section)
  ocrResults: Record<string, MappedField[]>
  setOcrResult: (sectionId: string, fields: MappedField[]) => void

  // Per-image results keyed by photoId (both AI and OCR)
  photoResults: Record<string, MappedField[]>
  setPhotoResult: (photoId: string, fields: MappedField[]) => void
  clearPhotoResult: (photoId: string) => void
  // Restore all AI/OCR metadata slices from a server response (e.g. GET /ai/results/{claimRefNo})
  restoreAiResults: (data: AiResultsResponse) => void

  // Photos pending server deletion — flushed on Save Draft (only for existing drafts).
  // allPhotos: true  → deleteAllClaimPhotos (vehicle-type change)
  // sections: [...]  → deleteClaimPhotosBySection (body-type change)
  pendingDeletions: {
    allPhotos: boolean
    sections: string[]
  }
  clearPendingDeletions: () => void

  // IDs of photos added in the current session since the last Save Draft or initial hydration.
  // Used to delete server-uploaded-but-unsaved photos on "Logout Without Saving".
  sessionNewPhotoIds: string[]
  deleteUnsavedServerPhotos: () => Promise<void>
  /** After login, remove any blob photos not present in the saved SectionImages list. */
  filterPhotosBySectionImages: (sectionImages: ClaimSectionImage[]) => Promise<void>

  // Derived helpers
  completedCount: () => number
  completionPercent: () => number
}

// Recomputes vehicleSectionsDirty from raw persisted state — used only once, right after
// IDB hydration, to recover "user already entered vehicle-specific data" across a page
// refresh that happened before any explicit field/photo action re-derived the flag in-session.
// Fields marked autoSet (programmatic defaults, e.g. trimLevel="Standard") don't count.
function computeVehicleSectionsDirty(
  formValues: FormValues,
  photos: PhotoItem[],
  fieldMetadata: Record<string, Record<string, FieldMetadata>>
): boolean {
  return VEHICLE_TYPE_SECTIONS.some((sid) => {
    const fields = formValues[sid] ?? {}
    const sectionMeta = fieldMetadata[sid] ?? {}
    const hasFields = Object.entries(fields).some(
      ([key, val]) =>
        key !== '_photoUrls' &&
        val !== '' &&
        !val.startsWith('Select') &&
        !sectionMeta[key]?.autoSet
    )
    const hasPhotos = photos.some((p) => p.sectionId === sid)
    return hasFields || hasPhotos
  })
}

// ── Sync blob URLs into formValues for a section ──────────────────────────────
function withPhotoUrls(
  formValues: FormValues,
  sectionId: string,
  sectionPhotos: PhotoItem[]
): FormValues {
  const urls = sectionPhotos.map((p) => p.blobUrl)
  return {
    ...formValues,
    [sectionId]: {
      ...(formValues[sectionId] ?? {}),
      _photoUrls: JSON.stringify(urls),
    },
  }
}

// ── Internal debounced IDB metadata save ──────────────────────────────────────
let _metaSaveTimer: ReturnType<typeof setTimeout> | null = null

function scheduleSave(state: ValuationState) {
  if (_metaSaveTimer) clearTimeout(_metaSaveTimer)
  _metaSaveTimer = setTimeout(() => {
    saveDraftMetadata({
      draftId: state.draftId,
      caseId: state.caseId,
      status: state.status,
      formValues: state.formValues,
      sectionStates: Object.fromEntries(
        Object.entries(state.sectionStates).map(([k, v]) => [
          k,
          { ...v, expanded: false }, // never restore expanded state
        ])
      ),
      hasTriedSubmit: state.hasTriedSubmit,
      isDirty: state.isDirty,
      fieldMetadata: state.fieldMetadata,
      aiResults: state.aiResults,
      aiRawPayloads: state.aiRawPayloads,
      ocrResults: state.ocrResults,
      photoResults: state.photoResults,
    })
  }, 300)
}

// Prevents concurrent loadPhotosFromServer calls for the same section from racing
const _loadingInProgress = new Set<string>()

// ── Store ─────────────────────────────────────────────────────────────────────

export const useValuationStore = create<ValuationState>()((set, get) => ({
  hydrated: false,
  isLoadingClaim: false,
  setIsLoadingClaim: (v) => set({ isLoadingClaim: v }),
  draftId: null,
  caseId: null,
  status: 'draft',
  sectionStates: buildInitialSectionStates(),
  formValues: {},
  photos: [],
   isDirty: false,
  vehicleSectionsDirty: false,
  markVehicleSectionsDirty: () => set({ vehicleSectionsDirty: true }),
  clearVehicleSectionsDirty: () => set({ vehicleSectionsDirty: false }),
  hasTriedSubmit: false,
  hasTriedSave: false,
  fieldMetadata: {},
  aiResults: {},
  aiRawPayloads: {},
  ocrResults: {},
  photoResults: {},
  pendingDeletions: { allPhotos: false, sections: [] },
  sessionNewPhotoIds: [],

  // ── Hydration ────────────────────────────────────────────────────────────
  markHydrated: () => set({ hydrated: true }),

  flushSave: async () => {
    if (_metaSaveTimer) {
      clearTimeout(_metaSaveTimer)
      _metaSaveTimer = null
    }
    const s = get()
    await saveDraftMetadata({
      draftId: s.draftId,
      caseId: s.caseId,
      status: s.status,
      formValues: s.formValues,
      sectionStates: Object.fromEntries(
        Object.entries(s.sectionStates).map(([k, v]) => [k, { ...v, expanded: false }])
      ),
      hasTriedSubmit: s.hasTriedSubmit,
      fieldMetadata: s.fieldMetadata,
      aiResults: s.aiResults,
      aiRawPayloads: s.aiRawPayloads,
      ocrResults: s.ocrResults,
      photoResults: s.photoResults,
    })
  },

  hydrateFromIDB: async () => {
    log.info('hydrateFromIDB: reading IDB')
    const [metadata, imageRecords] = await Promise.all([
      loadDraftMetadata(),
      loadAllImageRecords(),
    ])

    if (!metadata) {
      log.info('hydrateFromIDB: no IDB data found — starting fresh')
      set({ hydrated: true })
      return
    }

    log.info('hydrateFromIDB: IDB data loaded', {
      hasDraft: String(!!metadata.draftId),
      draftId: metadata.draftId ?? 'none',
      photoCount: String(imageRecords.length),
      hasFieldMetadata: String(Object.keys(metadata.fieldMetadata ?? {}).length > 0),
      hasAiResults: String(Object.keys(metadata.aiResults ?? {}).length > 0),
      hasPhotoResults: String(Object.keys(metadata.photoResults ?? {}).length > 0),
      hasOcrResults: String(Object.keys(metadata.ocrResults ?? {}).length > 0),
    })

    // Rebuild PhotoItem array — create ephemeral object URLs from stored blobs.
    // Backfill contentHash for records saved before this field was introduced.
    const photos: PhotoItem[] = await Promise.all(
      imageRecords.map(async (rec) => {
        let { contentHash } = rec
        if (!contentHash) {
          contentHash = await computeFileHash(rec.blob).catch(() => undefined)
          if (contentHash) {
            saveImageRecord({ ...rec, contentHash }).catch((e) => log.error('IDB save image record failed', e instanceof Error ? e : new Error(String(e))))
          }
        }
        return {
          id: rec.id,
          sectionId: rec.sectionId,
          type: rec.type,
          name: rec.name,
          mimeType: rec.mimeType,
          serverName: rec.serverName,
          blobSection: rec.blobSection,
          contentHash,
          blobUrl: URL.createObjectURL(rec.blob),
        }
      })
    )

    // Rebuild _photoUrls in formValues for each section that has images
    const uniqueSections = [...new Set(photos.map((p) => p.sectionId))]
    let restoredFormValues = metadata.formValues
    for (const sid of uniqueSections) {
      restoredFormValues = withPhotoUrls(restoredFormValues, sid, photos.filter((p) => p.sectionId === sid))
    }
    
    set({
      hydrated: true,
      draftId: metadata.draftId,
      caseId: metadata.caseId,
      status: metadata.status,
      formValues: restoredFormValues,
      isDirty: metadata.isDirty ?? false,
      vehicleSectionsDirty: computeVehicleSectionsDirty(restoredFormValues, photos, metadata.fieldMetadata ?? {}),
      // Restore section state but always collapsed
      sectionStates: Object.fromEntries(
        Object.entries(metadata.sectionStates).map(([k, v]) => [
          k,
          { ...v, expanded: false },
        ])
      ),
      hasTriedSubmit: metadata.hasTriedSubmit,
      photos,
      fieldMetadata: metadata.fieldMetadata ?? {},
      aiResults: metadata.aiResults ?? {},
      aiRawPayloads: metadata.aiRawPayloads ?? {},
      ocrResults: metadata.ocrResults ?? {},
      photoResults: metadata.photoResults ?? {},
    })

    log.info('hydrateFromIDB: complete', {
      photoCount: String(photos.length),
      aiResultSections: String(Object.keys(metadata.aiResults ?? {}).length),
      ocrResultSections: String(Object.keys(metadata.ocrResults ?? {}).length),
      photoResultCount: String(Object.keys(metadata.photoResults ?? {}).length),
    })
  },

  // ── Section actions ──────────────────────────────────────────────────────
  expandSection: (id) =>
    set((s) => ({
      sectionStates: {
        ...s.sectionStates,
        [id]: { ...s.sectionStates[id], expanded: true },
      },
    })),

  collapseSection: (id) =>
    set((s) => ({
      sectionStates: {
        ...s.sectionStates,
        [id]: { ...s.sectionStates[id], expanded: false },
      },
    })),

  toggleSection: (id) => {
    const current = get().sectionStates[id]
    const wasExpanded = current?.expanded ?? false

    const collapsed = Object.fromEntries(
      SECTIONS.map((s) => [
        s.id,
        { ...get().sectionStates[s.id], expanded: false },
      ])
    )

    set({
      sectionStates: {
        ...collapsed,
        [id]: { ...collapsed[id], expanded: !wasExpanded },
      },
    })
  },

  markSectionComplete: (id) => {
    set((s) => ({
      sectionStates: {
        ...s.sectionStates,
        [id]: { ...s.sectionStates[id], complete: true, expanded: false },
      },
    }))

    const idx = SECTIONS.findIndex((s) => s.id === id)
    if (idx < SECTIONS.length - 1) {
      setTimeout(() => get().toggleSection(SECTIONS[idx + 1].id), 360)
    }
  },

  // ── Field actions ────────────────────────────────────────────────────────
  setFieldValue: (sectionId, fieldId, value, autoSet) => {
    const prevValue = get().formValues[sectionId]?.[fieldId]
    const valueChanged = prevValue !== value

    set((s) => {
      const existingMeta = s.fieldMetadata[sectionId]?.[fieldId]
      const updatedMeta = autoSet
        ? { ...(existingMeta ?? { confidence: 0, source: 'auto' }), autoSet: true }
        : existingMeta
          ? { ...existingMeta, autoSet: false }
          : undefined

      return {
        formValues: {
          ...s.formValues,
          [sectionId]: {
            ...(s.formValues[sectionId] ?? {}),
            [fieldId]: value,
          },
        },
        fieldMetadata: updatedMeta
          ? {
            ...s.fieldMetadata,
            [sectionId]: {
              ...(s.fieldMetadata[sectionId] ?? {}),
              [fieldId]: updatedMeta,
            },
          }
          : s.fieldMetadata,
        sectionStates: s.sectionStates[sectionId]?.complete
          ? {
            ...s.sectionStates,
            [sectionId]: {
              ...s.sectionStates[sectionId],
              complete: false,
              touched: true,
            },
          }
          : s.sectionStates,
      }
    })
    if (!autoSet && valueChanged) set({ isDirty: true })
    if (!autoSet && VEHICLE_TYPE_SECTIONS.includes(sectionId)) set({ vehicleSectionsDirty: true })
    scheduleSave(get())
  },

  // ── Photo actions (blob-first) ───────────────────────────────────────────
  addPhoto: ({ sectionId, blob, type, name, mimeType, serverName, blobSection, contentHash }) => {
    const id = generateId()
    const blobUrl = URL.createObjectURL(blob)

    // Persist blob to IDB (fire-and-forget)
    saveImageRecord({ id, sectionId, type, name, mimeType, blob, serverName, blobSection, contentHash }).catch((e) => log.error('IDB save image record failed', e instanceof Error ? e : new Error(String(e))))

    set((s) => {
      const updatedPhotos = [...s.photos, { id, sectionId, blobUrl, type, name, mimeType, serverName, blobSection, contentHash }]
      return {
        photos: updatedPhotos,
        formValues: withPhotoUrls(s.formValues, sectionId, updatedPhotos.filter((p) => p.sectionId === sectionId)),
        isDirty: true,
        sessionNewPhotoIds: [...s.sessionNewPhotoIds, id],
        vehicleSectionsDirty: s.vehicleSectionsDirty || VEHICLE_TYPE_SECTIONS.includes(sectionId),
      }
    })
    scheduleSave(get())
    return id
  },

  removePhoto: async (photoId, _options) => {
    const photo = get().photos.find((p) => p.id === photoId)
    if (!photo) return
    const caseId = get().caseId ?? ''
    const userId = String(useAuthStore.getState().user?.userId ?? '')

    // Resolve blob path. Form-recognizer blobs live under {userId}/form-recognizer/ (no caseId).
    const effectiveUserId = userId
    const effectiveCaseId = caseId
    let section = photo.sectionId
    if (photo.blobSection === 'form-recognizer') {
      section = 'form-recognizer'
    } else if (photo.blobSection) {
      section = `${photo.blobSection}/${photo.sectionId}`
    }

    // Always delete from blob immediately — deferred deletion would cause removed images
    // to reappear on another browser/device since blob storage is the source of truth.
    if (effectiveUserId && effectiveCaseId && photo.serverName) {
      try {
        const res = await deleteClaimPhotoByName(effectiveUserId, effectiveCaseId, section, photo.serverName)
        if (!res?.data?.deleted) {
          useUiStore.getState().showToast(getMessage('photo.error.remove_failed'), 'error')
          return
        }
      } catch (err) {
        log.error('[removePhoto] blob delete failed', err instanceof Error ? err : new Error(String(err)))
        useUiStore.getState().showToast(getMessage('photo.error.remove_failed'), 'error')
        return
      }
    }

    URL.revokeObjectURL(photo.blobUrl)
    deleteImageRecord(photoId).catch((e) => log.error('IDB delete image record failed', e instanceof Error ? e : new Error(String(e))))
    set((s) => {
      const updatedPhotos = s.photos.filter((p) => p.id !== photoId)
      return {
        photos: updatedPhotos,
        formValues: withPhotoUrls(s.formValues, photo.sectionId, updatedPhotos.filter((p) => p.sectionId === photo.sectionId)),
        isDirty: true,
        sessionNewPhotoIds: s.sessionNewPhotoIds.filter((sid) => sid !== photoId),
        vehicleSectionsDirty: s.vehicleSectionsDirty || VEHICLE_TYPE_SECTIONS.includes(photo.sectionId),
      }
    })
    scheduleSave(get())
  },

  removePhotoLocal: (photoId) => {
    const photo = get().photos.find((p) => p.id === photoId)
    if (!photo) return
    URL.revokeObjectURL(photo.blobUrl)
    deleteImageRecord(photoId).catch((e) => log.error('[removePhotoLocal] IDB delete failed', e instanceof Error ? e : new Error(String(e))))
    set((s) => {
      const updatedPhotos = s.photos.filter((p) => p.id !== photoId)
      return {
        photos: updatedPhotos,
        formValues: withPhotoUrls(s.formValues, photo.sectionId, updatedPhotos.filter((p) => p.sectionId === photo.sectionId)),
        vehicleSectionsDirty: s.vehicleSectionsDirty || VEHICLE_TYPE_SECTIONS.includes(photo.sectionId),
      }
    })
  },

  clearAllPhotos: () => {
    const currentPhotos = get().photos
    const caseId = get().caseId ?? ''
    const draftId = get().draftId
    const userId = String(useAuthStore.getState().user?.userId ?? '')
    currentPhotos.forEach((p) => URL.revokeObjectURL(p.blobUrl))
    clearAllImageRecords().catch((e) => log.error('IDB clearAllImageRecords failed', e instanceof Error ? e : new Error(String(e))))
    // New Inspection: delete from server immediately
    if (userId && caseId && !draftId) {
      deleteAllClaimPhotos(userId, caseId).catch((e) => log.error('deleteAllClaimPhotos failed', e instanceof Error ? e : new Error(String(e))))
    }
    // Form Recognizer blobs live under {userId}/{caseId}/form-recognizer/, so deleteAllClaimPhotos
    // above already removes them for a new inspection. For a draft they must be kept.

    set((s) => {
      const updatedFormValues: FormValues = {}
      for (const [sid, fields] of Object.entries(s.formValues)) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { _photoUrls: _removed, ...rest } = fields
        updatedFormValues[sid] = rest
      }
      return {
        photos: [],
        formValues: updatedFormValues,
        // Existing draft: flag to call deleteAllClaimPhotos on next Save Draft
        pendingDeletions: (caseId && draftId)
          ? { ...s.pendingDeletions, allPhotos: true }
          : s.pendingDeletions,
      }
    })
  },

  clearSectionPhotos: (sectionId) => {
    const toRemove = get().photos.filter((p) => p.sectionId === sectionId)
    const caseId = get().caseId ?? ''
    const draftId = get().draftId
    const userId = String(useAuthStore.getState().user?.userId ?? '')
    toRemove.forEach((p) => {
      URL.revokeObjectURL(p.blobUrl)
      deleteImageRecord(p.id).catch((e) => log.error('IDB delete image record failed', e instanceof Error ? e : new Error(String(e))))
    })
    // New Inspection: delete from server immediately
    if (userId && caseId && !draftId && toRemove.length > 0) {
      const blobSections = new Set<string>()
      for (const p of toRemove) {
        if (p.blobSection === 'ocr') blobSections.add(`ocr/${sectionId}`)
        else blobSections.add(`ai/${sectionId}`)
      }
      deleteClaimPhotosBySection(userId, caseId, [...blobSections]).catch((e) => log.error('deleteClaimPhotosBySection failed', e instanceof Error ? e : new Error(String(e))))
    }

    // Compute blob-prefixed section paths (mirrors the new-inspection delete path)
    const blobSectionPaths = [...new Set(
      toRemove.map((p) => p.blobSection === 'ocr' ? `ocr/${sectionId}` : `ai/${sectionId}`)
    )]

    set((s) => {
      const updatedPhotos = s.photos.filter((p) => p.sectionId !== sectionId)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { _photoUrls: _removed, ...rest } = s.formValues[sectionId] ?? {}
      return {
        photos: updatedPhotos,
        formValues: { ...s.formValues, [sectionId]: rest },
        // Existing draft: add blob-prefixed section paths to delete on next Save Draft (deduped)
        pendingDeletions: (caseId && draftId)
          ? { ...s.pendingDeletions, sections: [...new Set([...s.pendingDeletions.sections, ...blobSectionPaths])] }
          : s.pendingDeletions,
      }
    })
  },


  // ── Server image sync ────────────────────────────────────────────────────
  loadPhotosFromServer: async (userId, caseId, sectionId, blobSection?) => {
    if (!userId || !caseId) return
    const lockKey = `${userId}:${caseId}:${sectionId}:${blobSection ?? ''}`
    if (_loadingInProgress.has(lockKey)) return
    _loadingInProgress.add(lockKey)
    try {
      const fetchSection = blobSection && blobSection !== sectionId
        ? `${blobSection}/${sectionId}`
        : sectionId
      const res = await getClaimImageList(userId, caseId, fetchSection)
      if (!res.success || !res.data?.images?.length) return
      const existingServerNames = new Set(
        get().photos
          .filter((p) => p.sectionId === sectionId)
          .map((p) => p.serverName)
          .filter(Boolean) as string[]
      )
      const { allPhotos, sections } = get().pendingDeletions
      for (const { name: serverName } of res.data.images) {
        if (existingServerNames.has(serverName)) continue
        // Skip photos whose section is pending bulk deletion (vehicle-type / body-type change)
        if (allPhotos || sections.includes(sectionId) || sections.includes(fetchSection)) continue
        let blob: Blob
        try {
          const imageUrl = await getClaimImageFile(userId, caseId, fetchSection, serverName)
          const fetchRes = await fetch(imageUrl)
          if (!fetchRes.ok) throw new Error(`HTTP ${fetchRes.status}`)
          blob = await fetchRes.blob()
        } catch (fetchErr) {
          log.error('[loadPhotosFromServer] Failed to fetch image', fetchErr instanceof Error ? fetchErr : new Error(String(fetchErr)), { serverName: String(serverName) })
          continue
        }
        const id = generateId()
        const blobUrl = URL.createObjectURL(blob)
        const mimeType = blob.type || undefined
        const contentHash = await computeFileHash(blob).catch(() => undefined)
        saveImageRecord({ id, sectionId, type: 'upload', serverName, mimeType, blob, blobSection, contentHash }).catch((e) => log.error('IDB save image record failed', e instanceof Error ? e : new Error(String(e))))
        set((s) => {
          const updatedPhotos = [...s.photos, { id, sectionId, blobUrl, type: 'upload' as const, serverName, mimeType, blobSection, contentHash }]
          return {
            photos: updatedPhotos,
            formValues: withPhotoUrls(s.formValues, sectionId, updatedPhotos.filter((p) => p.sectionId === sectionId)),
          }
        })
      }
    } catch (err) {
      log.error('[loadPhotosFromServer]', err instanceof Error ? err : new Error(String(err)))
    } finally {
      _loadingInProgress.delete(lockKey)
    }
  },

  loadAllPhotosFromServer: async (userId, caseId) => {
    if (!userId || !caseId) return
    log.info('[loadAllPhotosFromServer] Starting', { userId, caseId })
    const PHOTO_SECTIONS = ['standard-equipment', 'additional-options', 'body-information']
    const tasks: Promise<void>[] = []
    for (const sectionId of PHOTO_SECTIONS) {
      tasks.push(get().loadPhotosFromServer(userId, caseId, sectionId, 'ai'))
      tasks.push(get().loadPhotosFromServer(userId, caseId, sectionId, 'ocr'))
    }
    tasks.push(get().loadPhotosFromServer(userId, caseId, 'form-recognizer', 'form-recognizer'))
    await Promise.allSettled(tasks)
    log.info('[loadAllPhotosFromServer] Complete', { totalPhotos: String(get().photos.length) })
  },

  updatePhotoServerNames: async (userId, caseId, sectionId, blobSection?) => {
    if (!userId || !caseId) return
    try {
      const fetchSection = blobSection && blobSection !== sectionId ? `${blobSection}/${sectionId}` : (blobSection ?? sectionId)
      const res = await getClaimImageList(userId, caseId, fetchSection)
      if (!res.success) return
      const serverNames = (res.data?.images ?? []).map((img) => img.name)
      const updatesNeeded: Array<{ id: string; serverName: string }> = []
      set((s) => {
        const updatedPhotos = s.photos.map((photo) => {
          if (photo.sectionId !== sectionId || photo.serverName) return photo
          const matched = serverNames.find((sn) => photo.name && sn.endsWith(`-${photo.name}`))
          if (!matched) return photo
          updatesNeeded.push({ id: photo.id, serverName: matched })
          return { ...photo, serverName: matched, ...(blobSection ? { blobSection } : {}) }
        })
        return { photos: updatedPhotos }
      })
      updatesNeeded.forEach(({ id, serverName }) =>
        updateImageServerName(id, serverName, blobSection).catch((e) => log.error('IDB updateImageServerName failed', e instanceof Error ? e : new Error(String(e))))
      )
    } catch (err) {
      log.error('[updatePhotoServerNames]', err instanceof Error ? err : new Error(String(err)))
    }
  },

  clearPendingDeletions: () => set({ pendingDeletions: { allPhotos: false, sections: [] } }),

  setPhotoServerInfo: (photoId, info) => {
    set((s) => ({
      photos: s.photos.map((p) => p.id === photoId ? { ...p, ...info } : p),
    }))
    updateImageServerName(photoId, info.serverName, info.blobSection).catch((e) => log.error('IDB updateImageServerName failed', e instanceof Error ? e : new Error(String(e))))
  },

  // ── Submit flag ──────────────────────────────────────────────────────────
  setHasTriedSubmit: (value) => {
    set({ hasTriedSubmit: value })
    scheduleSave(get())
  },

  setHasTriedSave: (value) => {
    set({ hasTriedSave: value })
  },

  setIsDirty: (v) => {
    set({ isDirty: v, ...(v === false ? { sessionNewPhotoIds: [] } : {}) })
    scheduleSave(get())
  },

  // ── Draft lifecycle ──────────────────────────────────────────────────────
  setDraftId: (id) => {
    set({ draftId: id })
    scheduleSave(get())
  },
  setCaseId: (id) => {
    set({ caseId: id })
    scheduleSave(get())
  },

  // ── Claim data sync ──────────────────────────────────────────────────────
  setClaimData: (claimId, populated, baseline) => {
    set((s) => {
      let newFormValues: FormValues
      if (baseline) {
        // Smart merge: only apply server values for fields that haven't changed
        // since the mutation was sent (current value === baseline value).
        // This prevents a stale concurrent mutation from overwriting user edits.
        newFormValues = { ...s.formValues }
        for (const [sectionId, serverSection] of Object.entries(populated)) {
          const currentSection = s.formValues[sectionId] ?? {}
          const baseSection = baseline[sectionId] ?? {}
          const mergedSection: Record<string, string> = { ...currentSection }
          for (const [fieldId, serverValue] of Object.entries(serverSection)) {
            if ((currentSection[fieldId] ?? '') === (baseSection[fieldId] ?? '')) {
              mergedSection[fieldId] = serverValue as string
            }
          }
          newFormValues[sectionId] = mergedSection
        }
      } else {
        // Field-level deep merge: preserve existing keys within each section (e.g. _photoUrls)
        // while letting server values win for the same key.
        newFormValues = { ...s.formValues }
        for (const [sectionId, serverSection] of Object.entries(populated)) {
          const currentSection = s.formValues[sectionId] ?? {}
          newFormValues[sectionId] = { ...currentSection, ...serverSection }
        }
      }
      return { draftId: claimId, formValues: newFormValues }
    })
    scheduleSave(get())
  },

  loadClaimFromApi: async (claimId) => {
    try {
      const res = await getClaim(claimId)
      if (res.success && res.data) {
        await preloadRefCategories(res.data)
        const getRefLabel = useRefStore.getState().getLabelById
        const populated = claimResponseToFormValues(res.data, getRefLabel, useAttributeStore.getState().cache)
        get().setClaimData(claimId, populated)
      }
    } catch (err) {
      log.error('[valuationStore] Failed to load claim from API', err instanceof Error ? err : new Error(String(err)))
    }
  },

  setStatus: (status) => {
    set({ status })
    scheduleSave(get())
  },

  resetForm: () => {
    const caseId = get().caseId ?? ''
    const userId = String(useAuthStore.getState().user?.userId ?? '')
    const currentPhotos = get().photos

    // Revoke all object URLs to free memory
    currentPhotos.forEach((p) => URL.revokeObjectURL(p.blobUrl))

    // Clear IDB entirely (fire-and-forget)
    clearAllDraftData().catch((e) => log.error('IDB clearAllDraftData failed', e instanceof Error ? e : new Error(String(e))))

    // Delete all photos from DB + blob storage if userId and caseId exist
    if (userId && caseId) {
      deleteAllClaimPhotos(userId, caseId).catch((e) => log.error('deleteAllClaimPhotos failed', e instanceof Error ? e : new Error(String(e))))
    }
    // Form Recognizer blobs live under {userId}/{caseId}/form-recognizer/, so deleteAllClaimPhotos
    // above already removes them.

    useRefStore.getState().clearCache()       // Clear in-memory dropdown options
    useAttributeStore.getState().clearCache() // Clear in-memory field definitions

    set({
      draftId: null,
      caseId: uuidv4(),
      status: 'draft',
      sectionStates: buildInitialSectionStates(),
      formValues: {},
      photos: [],
      isDirty: false,
      vehicleSectionsDirty: false,
      hasTriedSubmit: false,
      hasTriedSave: false,
      fieldMetadata: {},
      aiResults: {},
      aiRawPayloads: {},
      ocrResults: {},
      photoResults: {},
      sessionNewPhotoIds: [],
      pendingDeletions: { allPhotos: false, sections: [] },
    })
  },

  clearLocalDraft: () => {
    get().photos.forEach((p) => URL.revokeObjectURL(p.blobUrl))
    clearAllDraftData().catch((e) => log.error('IDB clearAllDraftData failed', e instanceof Error ? e : new Error(String(e))))
    useRefStore.getState().clearCache()
    useAttributeStore.getState().clearCache()
    useUiStore.getState().clearApiFieldErrors()
    set({
      draftId: null,
      caseId: uuidv4(),
      status: 'draft',
      sectionStates: buildInitialSectionStates(),
      formValues: {},
      photos: [],
      vehicleSectionsDirty: false,
      hasTriedSubmit: false,
      hasTriedSave: false,
      fieldMetadata: {},
      aiResults: {},
      aiRawPayloads: {},
      ocrResults: {},
      photoResults: {},
      sessionNewPhotoIds: [],
      pendingDeletions: { allPhotos: false, sections: [] },
    })
  },

  deleteUnsavedServerPhotos: async () => {
    const { draftId, caseId, photos, sessionNewPhotoIds, pendingDeletions } = get()
    const userId = String(useAuthStore.getState().user?.userId ?? '')
    if (!userId || !caseId) return
    try {
      if (!draftId) {
        // New inspection: all uploaded photos are unsaved — delete the entire set
        await deleteAllClaimPhotos(userId, caseId)
      } else {
        // Existing draft: delete photos added this session and flush pending removals
        const ops: Promise<unknown>[] = []

        // Session-new photos (uploaded but not yet saved)
        const sessionSet = new Set(sessionNewPhotoIds)
        const toDelete = photos.filter((p) => p.serverName && sessionSet.has(p.id))
        toDelete.forEach((p) => {
          const section = p.blobSection === 'form-recognizer'
            ? 'form-recognizer'
            : p.blobSection ? `${p.blobSection}/${p.sectionId}` : p.sectionId
          ops.push(
            deleteClaimPhotoByName(userId, caseId, section, p.serverName!)
              .catch((e) => log.error('[deleteUnsavedServerPhotos] delete failed', e instanceof Error ? e : new Error(String(e))))
          )
        })

        // Pending bulk deletions (vehicle-type / body-type change, not yet saved via Save Draft)
        if (pendingDeletions.allPhotos) {
          await deleteAllClaimPhotos(userId, caseId)
        } else if (pendingDeletions.sections.length > 0) {
          ops.push(deleteClaimPhotosBySection(userId, caseId, pendingDeletions.sections))
          if (ops.length > 0) await Promise.allSettled(ops)
        }
      }
    } catch (e) {
      // Non-fatal: always proceed to logout
      log.error('[deleteUnsavedServerPhotos] failed', e instanceof Error ? e : new Error(String(e)))
    }
  },

  filterPhotosBySectionImages: async (sectionImages) => {
    const validNames = new Set(sectionImages.map((si) => si.imageName))
    const { photos, caseId } = get()
    const userId = String(useAuthStore.getState().user?.userId ?? '')
    if (!userId || !caseId) return

    const toRemove = photos.filter((p) => p.serverName && p.blobSection !== 'form-recognizer' && !validNames.has(p.serverName))
    if (toRemove.length === 0) return

    log.info('[filterPhotosBySectionImages] Removing unsaved server photos', { count: String(toRemove.length) })

    const deletionOps = toRemove.map((p) => {
      const section = p.blobSection === 'form-recognizer'
        ? 'form-recognizer'
        : p.blobSection
          ? `${p.blobSection}/${p.sectionId}`
          : p.sectionId
      return deleteClaimPhotoByName(userId, caseId, section, p.serverName!)
        .catch((e) => log.warn('[filterPhotosBySectionImages] blob delete failed (non-fatal)', { serverName: p.serverName!, error: String(e) }))
    })
    await Promise.allSettled(deletionOps)

    for (const p of toRemove) {
      URL.revokeObjectURL(p.blobUrl)
      deleteImageRecord(p.id).catch((e) => log.error('IDB delete failed', e instanceof Error ? e : new Error(String(e))))
    }

    const removeIds = new Set(toRemove.map((p) => p.id))
    set((s) => {
      const updatedPhotos = s.photos.filter((p) => !removeIds.has(p.id))
      const affectedSections = [...new Set(toRemove.map((p) => p.sectionId))]
      let fv = s.formValues
      for (const sid of affectedSections) {
        fv = withPhotoUrls(fv, sid, updatedPhotos.filter((p) => p.sectionId === sid))
      }
      return { photos: updatedPhotos, formValues: fv }
    })
  },

  // ── AI / OCR field population ────────────────────────────────────────────
  applyAIFields: (fields, forceOverwrite = false) => {
    set((state) => {
      const nextValues = { ...state.formValues }
      const nextMeta = { ...state.fieldMetadata }

      for (const mf of fields) {
        const currentVal = state.formValues[mf.sectionId]?.[mf.fieldKey]
        const hasValue = currentVal && currentVal !== '' && !currentVal.startsWith('Select')
        const willWrite = !hasValue || forceOverwrite

        // Spread existing field meta so aiValue is preserved across re-runs when not overwriting.
        // aiValue is recorded only when AI actually writes the field, enabling retrain diff later.
        nextMeta[mf.sectionId] = {
          ...(nextMeta[mf.sectionId] ?? {}),
          [mf.fieldKey]: {
            ...(nextMeta[mf.sectionId]?.[mf.fieldKey] ?? {}),
            confidence: mf.confidence,
            source: mf.source,
            hideConfidence: false,
            ...(mf.pendingMatch ? { rawAIValue: mf.value } : {}),
            ...(willWrite ? { aiValue: mf.value } : {}),
          },
        }

        if (!willWrite) continue

        nextValues[mf.sectionId] = {
          ...(nextValues[mf.sectionId] ?? {}),
          [mf.fieldKey]: mf.value,
        }
      }

      scheduleSave({ ...state, formValues: nextValues })
      return { formValues: nextValues, fieldMetadata: nextMeta }
    })
  },

  applyFieldMetadata: (metadata) => {
    set({ fieldMetadata: metadata })
    scheduleSave(get())
  },

  clearFieldMetadata: () => set({ fieldMetadata: {} }),

  // Selectively removes only the specified field keys from formValues and fieldMetadata.
  // Used when removing a photo: only fields whose current form value still matches the
  // AI/OCR-detected value (i.e. the user has not manually overridden them) are passed in,
  // so we avoid wiping fields the user intentionally edited after the scan.
  clearSpecificFields: (fields) => {
    set((s) => {
      const nextFormValues = { ...s.formValues }
      const nextMeta = { ...s.fieldMetadata }
      for (const { sectionId, fieldKey } of fields) {
        if (nextFormValues[sectionId]) {
          const sec = { ...nextFormValues[sectionId] }
          delete sec[fieldKey]
          nextFormValues[sectionId] = sec
        }
        if (nextMeta[sectionId]) {
          const meta = { ...nextMeta[sectionId] }
          delete meta[fieldKey]
          nextMeta[sectionId] = meta
        }
      }
      return { formValues: nextFormValues, fieldMetadata: nextMeta }
    })
    scheduleSave(get())
  },

  promoteFieldConfidence: (updates) => {
    set((s) => {
      const nextMeta = { ...s.fieldMetadata }
      for (const { sectionId, fieldKey, confidence } of updates) {
        const existing = nextMeta[sectionId]?.[fieldKey]
        if (!existing) continue
        nextMeta[sectionId] = { ...nextMeta[sectionId], [fieldKey]: { ...existing, confidence } }
      }
      return { fieldMetadata: nextMeta }
    })
    scheduleSave(get())
  },

  clearRawAIValue: (sectionId, fieldId) => {
    set((s) => {
      const meta = s.fieldMetadata[sectionId]?.[fieldId]
      if (!meta?.rawAIValue) return s
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { rawAIValue: _, ...rest } = meta
      return {
        fieldMetadata: {
          ...s.fieldMetadata,
          [sectionId]: { ...s.fieldMetadata[sectionId], [fieldId]: rest },
        },
      }
    })
    scheduleSave(get())
  },

  setAiValue: (sectionId, fieldId, value) => {
    set((s) => {
      const meta = s.fieldMetadata[sectionId]?.[fieldId]
      if (!meta) return s
      return {
        fieldMetadata: {
          ...s.fieldMetadata,
          [sectionId]: { ...s.fieldMetadata[sectionId], [fieldId]: { ...meta, aiValue: value } },
        },
      }
    })
  },

  setScanResult: (sectionId, fieldId, value) => {
      set((s) => ({
        fieldMetadata: {
          ...s.fieldMetadata,
          [sectionId]: {
            ...(s.fieldMetadata[sectionId] ?? {}),
            [fieldId]: { ...(s.fieldMetadata[sectionId]?.[fieldId] ?? {} as FieldMetadata), scanResult: value },
          },
        },
      }))
      scheduleSave(get())
    },
  
    clearScanResult: (sectionId, fieldId) => {
      set((s) => {
        const meta = s.fieldMetadata[sectionId]?.[fieldId]
        if (!meta?.scanResult) return s
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { scanResult: _, ...rest } = meta
        return {
          fieldMetadata: {
            ...s.fieldMetadata,
            [sectionId]: { ...s.fieldMetadata[sectionId], [fieldId]: rest },
          },
        }
      })
      scheduleSave(get())
    },

  setAiResult: (sectionId, fields) => {
    set((s) => {
      const existing = s.aiResults[sectionId] ?? []
      const merged = new Map(existing.map((f) => [f.fieldKey, f]))
      for (const f of fields) merged.set(f.fieldKey, f)
      return { aiResults: { ...s.aiResults, [sectionId]: [...merged.values()] } }
    })
    scheduleSave(get())
  },

  setOcrResult: (sectionId, fields) => {
    set((s) => {
      const existing = s.ocrResults[sectionId] ?? []
      const merged = new Map(existing.map((f) => [f.fieldKey, f]))
      for (const f of fields) merged.set(f.fieldKey, f)
      return { ocrResults: { ...s.ocrResults, [sectionId]: [...merged.values()] } }
    })
    scheduleSave(get())
  },

  setPhotoResult: (photoId, fields) => {
    set((s) => ({ photoResults: { ...s.photoResults, [photoId]: fields } }))
    scheduleSave(get())
  },

  clearPhotoResult: (photoId) => {
    set((s) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [photoId]: _removed, ...remaining } = s.photoResults
      return { photoResults: remaining }
    })
    scheduleSave(get())
  },

  setAiRawPayload: (photoSectionId, payload) => {
    set((s) => ({ aiRawPayloads: { ...s.aiRawPayloads, [photoSectionId]: payload } }))
    scheduleSave(get())
  },

  clearSectionAIData: (photoSectionId) => {
    set((s) => {
      // aiResults[photoSectionId] holds every MappedField the AI detected from this
      // photo section's images — fields may live in any section of the form.
      const affectedFields = s.aiResults[photoSectionId] ?? []

      const nextFormValues = { ...s.formValues }
      const nextFieldMetadata = { ...s.fieldMetadata }

      // These fields drive section visibility and dropdowns; never auto-clear them.
      const PROTECTED_FIELDS = new Set(['vehicleType', 'use', 'type'])

      // Group affected fields by the section they live in
      const bySectionId: Record<string, string[]> = {}
      for (const mf of affectedFields) {
        if (!PROTECTED_FIELDS.has(mf.fieldKey)) {
          (bySectionId[mf.sectionId] ??= []).push(mf.fieldKey)
        }
      }

      for (const [sid, fieldIds] of Object.entries(bySectionId)) {
        // Clear form values for AI-detected fields in this section
        const sectionValues = { ...nextFormValues[sid] }
        for (const id of fieldIds) delete sectionValues[id]
        nextFormValues[sid] = sectionValues

        // Remove fieldMetadata entries for those fields
        const sectionMeta = { ...nextFieldMetadata[sid] }
        for (const id of fieldIds) delete sectionMeta[id]
        if (Object.keys(sectionMeta).length === 0) delete nextFieldMetadata[sid]
        else nextFieldMetadata[sid] = sectionMeta
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [photoSectionId]: _result, ...remainingResults } = s.aiResults
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [photoSectionId]: _payload, ...remainingPayloads } = s.aiRawPayloads

      // Clear photoResults for all AI photos in this section
      const nextPhotoResultsAI = { ...s.photoResults }
      for (const p of s.photos) {
        if (p.sectionId === photoSectionId && p.blobSection !== 'ocr') {
          delete nextPhotoResultsAI[p.id]
        }
      }

      return {
        formValues: nextFormValues,
        fieldMetadata: nextFieldMetadata,
        aiResults: remainingResults,
        aiRawPayloads: remainingPayloads,
        photoResults: nextPhotoResultsAI,
      }
    })
    scheduleSave(get())
  },

  clearSectionOcrData: (sectionId) => {
    set((s) => {
      const affectedFields = s.ocrResults[sectionId] ?? []
      const nextFormValues = { ...s.formValues }
      const nextFieldMetadata = { ...s.fieldMetadata }

      // These fields drive section visibility and dropdowns; never auto-clear them.
      const PROTECTED_FIELDS = new Set(['vehicleType', 'use', 'type'])

      const bySectionId: Record<string, string[]> = {}
      for (const mf of affectedFields) {
        if (!PROTECTED_FIELDS.has(mf.fieldKey)) {
          (bySectionId[mf.sectionId] ??= []).push(mf.fieldKey)
        }
      }

      for (const [sid, fieldIds] of Object.entries(bySectionId)) {
        const sectionValues = { ...nextFormValues[sid] }
        for (const id of fieldIds) delete sectionValues[id]
        nextFormValues[sid] = sectionValues

        const sectionMeta = { ...nextFieldMetadata[sid] }
        for (const id of fieldIds) delete sectionMeta[id]
        if (Object.keys(sectionMeta).length === 0) delete nextFieldMetadata[sid]
        else nextFieldMetadata[sid] = sectionMeta
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [sectionId]: _ocrResult, ...remainingOcrResults } = s.ocrResults

      // Also clear photoResults for OCR/form-recognizer photos in this section
      const nextPhotoResultsOCR = { ...s.photoResults }
      for (const p of s.photos) {
        if (p.sectionId === sectionId && (p.blobSection === 'ocr' || p.blobSection === 'form-recognizer')) delete nextPhotoResultsOCR[p.id]
      }

      return {
        formValues: nextFormValues,
        fieldMetadata: nextFieldMetadata,
        ocrResults: remainingOcrResults,
        photoResults: nextPhotoResultsOCR,
      }
    })
    scheduleSave(get())
  },

  clearSectionAIMetadata: (photoSectionId) => {
    set((s) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [photoSectionId]: _result, ...remainingResults } = s.aiResults
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [photoSectionId]: _payload, ...remainingPayloads } = s.aiRawPayloads
      const nextPhotoResults = { ...s.photoResults }
      for (const p of s.photos) {
        if (p.sectionId === photoSectionId && p.blobSection !== 'ocr') delete nextPhotoResults[p.id]
      }
      return { aiResults: remainingResults, aiRawPayloads: remainingPayloads, photoResults: nextPhotoResults }
    })
    scheduleSave(get())
  },

  clearSectionOcrMetadata: (sectionId) => {
    set((s) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [sectionId]: _ocrResult, ...remainingOcrResults } = s.ocrResults
      const nextPhotoResults = { ...s.photoResults }
      for (const p of s.photos) {
        if (p.sectionId === sectionId && (p.blobSection === 'ocr' || p.blobSection === 'form-recognizer')) delete nextPhotoResults[p.id]
      }
      return { ocrResults: remainingOcrResults, photoResults: nextPhotoResults }
    })
    scheduleSave(get())
  },

  clearAllAIData: () => {
    set({ fieldMetadata: {}, aiResults: {}, aiRawPayloads: {}, ocrResults: {}, photoResults: {} })
    scheduleSave(get())
  },

  restoreAiResults: (data) => {
    log.info('restoreAiResults: remapping server AI/OCR metadata to local photo IDs', {
      serverPhotoResultCount: String(Object.keys(data.photoResults ?? {}).length),
      aiResultSections: String(Object.keys(data.aiResults ?? {}).length),
      ocrResultSections: String(Object.keys(data.ocrResults ?? {}).length),
      localPhotoCount: String(get().photos.length),
    })
    const currentPhotos = get().photos
    // photoResults arrives keyed by serverName (stable across devices).
    // Remap to photoId (ephemeral, per-session) so PhotoStrip badge lookups work.
    // Also rebuild aiResults keyed by photo.sectionId (the upload section) so that
    // each section's aggregate view reflects which photos were uploaded there, rather
    // than relying on the server's aiResults which may be keyed by MappedField.sectionId.
    const remappedPhotoResults: Record<string, MappedField[]> = {}
    const rebuiltAiResults: Record<string, MappedField[]> = { ...(data.aiResults ?? {}) }
    for (const [serverName, results] of Object.entries(data.photoResults ?? {})) {
      const photo = currentPhotos.find((p) => p.serverName === serverName)
      if (!photo) continue
      remappedPhotoResults[photo.id] = results
      // Keep aiResults keyed by upload section (photo.sectionId), not by MappedField.sectionId,
      // so that clearBodyInfoAIData and section-level aggregate views work correctly.
      if (photo.blobSection === 'ai') {
        const existing = rebuiltAiResults[photo.sectionId] ?? []
        const merged = new Map(existing.map((f) => [f.fieldKey, f]))
        for (const f of results) merged.set(f.fieldKey, f)
        rebuiltAiResults[photo.sectionId] = [...merged.values()]
      }
    }
    const unmappedCount = Object.keys(data.photoResults ?? {}).length - Object.keys(remappedPhotoResults).length
    if (unmappedCount > 0) {
      log.warn('restoreAiResults: some server photo results had no matching local photo', {
        unmappedCount: String(unmappedCount),
      })
    }
    log.info('restoreAiResults: complete', {
      mappedPhotoResults: String(Object.keys(remappedPhotoResults).length),
      rebuiltAiResultSections: String(Object.keys(rebuiltAiResults).length),
    })
    set({
      fieldMetadata: data.fieldMetadata ?? {},
      aiResults: rebuiltAiResults,
      aiRawPayloads: data.aiRawPayloads ?? {},
      ocrResults: data.ocrResults ?? {},
      photoResults: remappedPhotoResults,
    })
    scheduleSave(get())
  },

  clearBodyInfoAIData: () => {
    set((s) => {
      const nextAiResults: Record<string, MappedField[]> = {}
      for (const [photoSectionId, fields] of Object.entries(s.aiResults)) {
        const filtered = fields.filter((f) => f.sectionId !== 'body-information')
        if (filtered.length > 0) nextAiResults[photoSectionId] = filtered
      }
      const nextOcrResults: Record<string, MappedField[]> = {}
      for (const [photoSectionId, fields] of Object.entries(s.ocrResults)) {
        const filtered = fields.filter((f) => f.sectionId !== 'body-information')
        if (filtered.length > 0) nextOcrResults[photoSectionId] = filtered
      }
      // Clear photoResults for body-information photos
      const nextPhotoResults = { ...s.photoResults }
      for (const p of s.photos) {
        if (p.sectionId === 'body-information') delete nextPhotoResults[p.id]
      }
      return { aiResults: nextAiResults, aiRawPayloads: {}, ocrResults: nextOcrResults, photoResults: nextPhotoResults }
    })
    scheduleSave(get())
  },

  // ── Derived helpers ──────────────────────────────────────────────────────
  completedCount: () => {
    const states = get().sectionStates
    return Object.values(states).filter((s) => s.complete).length
  },

  completionPercent: () => {
    const done = get().completedCount()
    return Math.round((done / SECTIONS.length) * 100)
  },

  resetSectionFields: (sectionId) => {
    set((s) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [sectionId]: _removedMeta, ...remainingMeta } = s.fieldMetadata
      return {
        formValues: { ...s.formValues, [sectionId]: {} },
        fieldMetadata: remainingMeta,
      }
    })
    scheduleSave(get())
  },
}))

// Auto-save metadata on sectionStates changes (mark-complete, collapse, etc.)
useValuationStore.subscribe((state) => {
  if (state.hydrated) {
    scheduleSave(state)
  }
})
